/**
 * PinPanelDock.jsx — Floating bottom dock with auto-pinned client chips.
 *
 * Behaviour (user choice 1.b):
 *   • Any client whose Control Panel was opened in the last 24h auto-appears
 *     as a chip in this dock.
 *   • Chips are stored in localStorage under `gtw_cp_pins`, ordered by last
 *     opened (most recent first). Up to 12 chips, oldest auto-evicted.
 *   • Clicking a chip opens (or re-focuses) that client's panel in a new tab.
 *   • Each chip has an X to unpin manually.
 *   • Dock is only visible for admin/staff and only when at least 1 pin exists.
 *   • Dock auto-hides on print + on the panel itself (it would be redundant
 *     when you're already there).
 */
import { useEffect, useMemo, useState, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Pin, X as XIcon, Layers } from "lucide-react";

const KEY = "gtw_cp_pins";
const MAX_PINS = 12;
const TTL_MS = 24 * 60 * 60 * 1000;        // 24h auto-eviction

const readPins = () => {
    try {
        const raw = localStorage.getItem(KEY);
        if (!raw) return [];
        const now = Date.now();
        return JSON.parse(raw)
            .filter((p) => p && p.id && p.opened_at && (now - p.opened_at) < TTL_MS)
            .slice(0, MAX_PINS);
    } catch { return []; }
};
const writePins = (pins) => {
    try { localStorage.setItem(KEY, JSON.stringify(pins.slice(0, MAX_PINS))); } catch { /* quota */ }
};

/** Public helper called from ClientControlPanel.jsx on mount to auto-pin. */
export const pinClient = (client) => {
    if (!client?.id) return;
    const existing = readPins().filter((p) => p.id !== client.id);
    const next = [{
        id: client.id,
        name: client.trade_name || client.name || "Client",
        code: client.code_no || client.identifier || "",
        opened_at: Date.now(),
    }, ...existing].slice(0, MAX_PINS);
    writePins(next);
    // Broadcast so other tabs/components can update
    try { window.dispatchEvent(new CustomEvent("gtw-pins-changed")); } catch { /* noop */ }
};

export default function PinPanelDock() {
    const { user } = useAuth();
    const location = useLocation();
    const navigate = useNavigate();
    const [pins, setPins] = useState(readPins());
    const [collapsed, setCollapsed] = useState(false);

    const refresh = useCallback(() => {
        const fresh = readPins();
        setPins(fresh);
        // Housekeeping: write back the filtered list so stale (>24h) entries
        // don't bloat localStorage indefinitely.
        try {
            const raw = localStorage.getItem(KEY);
            if (raw && JSON.parse(raw).length !== fresh.length) writePins(fresh);
        } catch { /* noop */ }
    }, []);

    useEffect(() => {
        const onChange = () => refresh();
        const onStorage = (e) => { if (e.key === KEY) refresh(); };
        window.addEventListener("gtw-pins-changed", onChange);
        window.addEventListener("storage", onStorage);
        // Re-check TTL on focus (browser tab may have been backgrounded for hours)
        window.addEventListener("focus", refresh);
        return () => {
            window.removeEventListener("gtw-pins-changed", onChange);
            window.removeEventListener("storage", onStorage);
            window.removeEventListener("focus", refresh);
        };
    }, [refresh]);

    const open = (p) => navigate(`/clients/${p.id}/panel`);
    const unpin = (p) => {
        const next = readPins().filter((x) => x.id !== p.id);
        writePins(next);
        setPins(next);
    };
    const clearAll = () => { writePins([]); setPins([]); };

    const role = user?.role;
    const onPanelPage = /\/clients\/[^/]+\/panel$/.test(location.pathname);
    const visible = (role === "admin" || role === "staff") && pins.length > 0 && !onPanelPage;

    if (!visible) return null;

    return (
        <div className="fixed bottom-3 left-1/2 -translate-x-1/2 z-40 print:hidden" data-testid="pin-dock">
            <div className={`bg-[#0A1128]/95 backdrop-blur ring-1 ring-amber-400/40 rounded-full shadow-2xl flex items-center gap-1 px-2 py-1.5 transition-all ${collapsed ? "" : "pr-3"}`}>
                <button onClick={() => setCollapsed((c) => !c)} className="w-7 h-7 rounded-full bg-amber-500/20 text-amber-300 flex items-center justify-center hover:bg-amber-500/40" title="Pinned Clients" data-testid="pin-dock-toggle">
                    <Pin size={13} />
                </button>
                {!collapsed && pins.map((p) => (
                    <div key={p.id} className="group flex items-center bg-white/10 hover:bg-amber-500/20 text-white rounded-full text-[11px] font-bold transition-colors" data-testid={`pin-chip-${p.id}`}>
                        <button onClick={() => open(p)} className="pl-3 pr-1 py-1.5 inline-flex items-center gap-1.5" title={`${p.name}${p.code ? ` (${p.code})` : ""}`} data-testid={`pin-open-${p.id}`}>
                            <Layers size={11} className="text-amber-300" />
                            <span className="max-w-[140px] truncate">{p.name}</span>
                            {p.code && <span className="text-[9px] font-mono text-slate-300">{p.code}</span>}
                        </button>
                        <button onClick={() => unpin(p)} className="opacity-50 group-hover:opacity-100 px-1.5 py-1.5 text-slate-300 hover:text-rose-400" title="Unpin" data-testid={`pin-close-${p.id}`}>
                            <XIcon size={10} />
                        </button>
                    </div>
                ))}
                {!collapsed && pins.length > 1 && (
                    <button onClick={clearAll} className="text-[10px] text-slate-400 hover:text-rose-400 ml-1 px-2" title="Clear all pins" data-testid="pin-clear-all">clear</button>
                )}
            </div>
        </div>
    );
}
